// src/components/coupon/CouponUpsertDialog.tsx
import * as React from "react";
import { Button, DatePicker, Input, Modal, Select, Space, Spin, Tag, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import type { CouponDetail, CouponListItem, CouponUpsertPayload } from "src/api/coupon/couponApi";
import BrandSelectModal, { type SelectedBrand } from "./BrandSelectModal";
import CollectionSelectModal, { type SelectedCollection } from "./CollectionSelectModal";
import ProductSelectModal, { type SelectedProduct } from "./ProductSelectModal";
import AppInput from "src/components/common/AppInput";
import AppInputNumber from "src/components/common/AppInputNumber";

type Props = {
  open: boolean;
  item: CouponDetail | null;
  listItem?: CouponListItem | null;
  loading: boolean;
  onClose: () => void;
  onSubmit: (payload: CouponUpsertPayload) => Promise<void>;
};

export default function CouponUpsertDialog({
  open,
  item,
  listItem,
  loading,
  onClose,
  onSubmit,
}: Props) {
  const [formData, setFormData] = React.useState<CouponUpsertPayload>({
    couponName: "",
    couponCode: "",
    couponType: "PUBLIC",
    discountType: "FIXED",
    discountValue: 0,
    maxDiscountAmount: 0,
    minOrderAmount: 0,
    maxOrderAmount: 0,
    couponScope: "CART",
    applicableCollectionIds: [],
    applicableProductIds: [],
    applicableBrandIds: [],
    excludedCollectionIds: [],
    excludedProductIds: [],
    excludedBrandIds: [],
    issueLimit: null,
    expiredAt: "",
    availableDay: 0,
    description: "",
  });

  const [expiredAtDayjs, setExpiredAtDayjs] = React.useState<Dayjs | null>(null);

  const [brandModalOpen, setBrandModalOpen] = React.useState(false);
  const [collectionModalOpen, setCollectionModalOpen] = React.useState(false);
  const [productModalOpen, setProductModalOpen] = React.useState(false);
  const [excludedBrandModalOpen, setExcludedBrandModalOpen] = React.useState(false);
  const [excludedCollectionModalOpen, setExcludedCollectionModalOpen] = React.useState(false);
  const [excludedProductModalOpen, setExcludedProductModalOpen] = React.useState(false);

  const [selectedBrands, setSelectedBrands] = React.useState<SelectedBrand[]>([]);
  const [selectedCollections, setSelectedCollections] = React.useState<SelectedCollection[]>([]);
  const [selectedProducts, setSelectedProducts] = React.useState<SelectedProduct[]>([]);
  const [selectedExcludedBrands, setSelectedExcludedBrands] = React.useState<SelectedBrand[]>([]);
  const [selectedExcludedCollections, setSelectedExcludedCollections] = React.useState<
    SelectedCollection[]
  >([]);
  const [selectedExcludedProducts, setSelectedExcludedProducts] = React.useState<SelectedProduct[]>(
    []
  );

  React.useEffect(() => {
    if (open && item) {
      const collectionIds = item.applicableCollections?.map((c) => c.collectionId) || [];
      const brandIds = item.applicableBrands?.map((b) => b.brandId) || [];
      const productIds = item.applicableProducts?.map((p) => p.productId) || [];
      const excludedCollectionIds = item.excludedCollections?.map((c) => c.collectionId) || [];
      const excludedBrandIds = item.excludedBrands?.map((b) => b.brandId) || [];
      const excludedProductIds = item.excludedProducts?.map((p) => p.productId) || [];

      setFormData({
        couponName: item.couponName || "",
        couponCode: item.couponCode || "",
        couponType: item.couponType || "PUBLIC",
        discountType: item.discountType || "FIXED",
        discountValue: item.discountValue || 0,
        maxDiscountAmount: item.maxDiscountAmount || 0,
        minOrderAmount: item.minOrderAmount || 0,
        maxOrderAmount: item.maxOrderAmount || 0,
        couponScope: item.couponScope || "CART",
        applicableCollectionIds: collectionIds,
        applicableProductIds: productIds,
        applicableBrandIds: brandIds,
        excludedCollectionIds: excludedCollectionIds,
        excludedProductIds: excludedProductIds,
        excludedBrandIds: excludedBrandIds,
        issueLimit: item.issueLimit ?? null,
        expiredAt: item.expiredAt || "",
        availableDay: item.availableDay || 0,
        description: item.description || "",
      });
      setExpiredAtDayjs(item.expiredAt ? dayjs(item.expiredAt) : null);
      setSelectedBrands(
        item.applicableBrands?.map((brand) => ({
          id: brand.brandId,
          name: brand.brandName,
        })) || []
      );
      setSelectedCollections(
        item.applicableCollections?.map((collection) => ({
          id: collection.collectionId,
          name: collection.collectionName,
        })) || []
      );
      setSelectedProducts(
        item.applicableProducts?.map((product) => ({
          id: product.productId,
          name: product.productName,
        })) || []
      );
      setSelectedExcludedBrands(
        item.excludedBrands?.map((brand) => ({
          id: brand.brandId,
          name: brand.brandName,
        })) || []
      );
      setSelectedExcludedCollections(
        item.excludedCollections?.map((collection) => ({
          id: collection.collectionId,
          name: collection.collectionName,
        })) || []
      );
      setSelectedExcludedProducts(
        item.excludedProducts?.map((product) => ({
          id: product.productId,
          name: product.productName,
        })) || []
      );
    } else if (open && !item) {
      setFormData({
        couponName: "",
        couponCode: "",
        couponType: "PUBLIC",
        discountType: "FIXED",
        discountValue: 0,
        maxDiscountAmount: 0,
        minOrderAmount: 0,
        maxOrderAmount: 0,
        couponScope: "CART",
        applicableCollectionIds: [],
        applicableProductIds: [],
        applicableBrandIds: [],
        excludedCollectionIds: [],
        excludedProductIds: [],
        excludedBrandIds: [],
        issueLimit: null,
        expiredAt: "",
        availableDay: 0,
        description: "",
      });
      setExpiredAtDayjs(null);
      setSelectedBrands([]);
      setSelectedCollections([]);
      setSelectedProducts([]);
      setSelectedExcludedBrands([]);
      setSelectedExcludedCollections([]);
      setSelectedExcludedProducts([]);
    }
  }, [open, item]);

  const handleChange = (field: keyof CouponUpsertPayload, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.couponName.trim()) {
      alert("쿠폰명을 입력해주세요.");
      return;
    }
    if (!formData.couponCode.trim()) {
      alert("쿠폰코드를 입력해주세요.");
      return;
    }
    if (formData.discountValue <= 0) {
      alert("할인값을 입력해주세요.");
      return;
    }

    const payload: CouponUpsertPayload = {
      ...formData,
      expiredAt: expiredAtDayjs ? expiredAtDayjs.toISOString() : "",
      applicableCollectionIds: formData.applicableCollectionIds || [],
      applicableProductIds: formData.applicableProductIds || [],
      applicableBrandIds: formData.applicableBrandIds || [],
      excludedCollectionIds: formData.excludedCollectionIds || [],
      excludedProductIds: formData.excludedProductIds || [],
      excludedBrandIds: formData.excludedBrandIds || [],
    };

    await onSubmit(payload);
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        title={listItem ? "쿠폰 수정" : "쿠폰 등록"}
        width={900}
        centered
        footer={
          <Space>
            <Button onClick={onClose}>닫기</Button>
            <Button type="primary" onClick={handleSubmit} disabled={loading}>
              {listItem ? "수정" : "등록"}
            </Button>
          </Space>
        }
      >
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
            <Spin />
          </div>
        ) : (
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <AppInput
              value={formData.couponName}
              onChange={(e) => handleChange("couponName", e.target.value)}
              placeholder="쿠폰명"
            />

            <AppInput
              value={formData.couponCode}
              onChange={(e) => handleChange("couponCode", e.target.value)}
              placeholder="쿠폰코드"
            />

            <Select
              value={formData.couponType}
              onChange={(value) => handleChange("couponType", value)}
              options={[
                { value: "PUBLIC", label: "공개" },
                { value: "PRIVATE", label: "개인" },
                { value: "AUTO", label: "자동발급" },
                { value: "EVENT", label: "이벤트" },
                { value: "FREE_SHIPPING", label: "무료 배송" },
              ]}
              style={{ width: 200 }}
            />

            <Select
              value={formData.discountType}
              onChange={(value) => handleChange("discountType", value)}
              options={[
                { value: "FIXED", label: "고정금액" },
                { value: "PERCENTAGE", label: "비율" },
              ]}
              style={{ width: 200 }}
            />

            <AppInputNumber
              value={formData.discountValue}
              onChange={(value) => handleChange("discountValue", Number(value ?? 0))}
              style={{ width: "100%" }}
              placeholder={formData.discountType === "PERCENTAGE" ? "할인율 (%)" : "할인금액 (원)"}
            />

            <AppInputNumber
              value={formData.maxDiscountAmount}
              onChange={(value) => handleChange("maxDiscountAmount", Number(value ?? 0))}
              style={{ width: "100%" }}
              placeholder="최대 할인금액"
            />

            <AppInputNumber
              value={formData.minOrderAmount}
              onChange={(value) => handleChange("minOrderAmount", Number(value ?? 0))}
              style={{ width: "100%" }}
              placeholder="최소 주문금액"
            />

            <AppInputNumber
              value={formData.maxOrderAmount}
              onChange={(value) => handleChange("maxOrderAmount", Number(value ?? 0))}
              style={{ width: "100%" }}
              placeholder="최대 주문금액"
            />

            <Select
              value={formData.couponScope}
              onChange={(value) => handleChange("couponScope", value)}
              options={[
                { value: "CART", label: "장바구니" },
                { value: "PRODUCT", label: "상품" },
              ]}
              style={{ width: 200 }}
            />

            <Space direction="vertical" size={6}>
              <Typography.Text strong>적용 대상</Typography.Text>
              <Space>
                <Button onClick={() => setBrandModalOpen(true)}>브랜드 선택</Button>
                <Button onClick={() => setCollectionModalOpen(true)}>컬렉션 선택</Button>
                <Button onClick={() => setProductModalOpen(true)}>상품 선택</Button>
              </Space>

              <Space wrap>
                {selectedBrands.map((brand) => (
                  <Tag
                    key={`brand-${brand.id}`}
                    closable
                    onClose={() => {
                      const next = selectedBrands.filter((b) => b.id !== brand.id);
                      setSelectedBrands(next);
                      handleChange(
                        "applicableBrandIds",
                        next.map((b) => b.id)
                      );
                    }}
                  >
                    {brand.name}
                  </Tag>
                ))}
                {selectedCollections.map((collection) => (
                  <Tag
                    key={`collection-${collection.id}`}
                    closable
                    onClose={() => {
                      const next = selectedCollections.filter((c) => c.id !== collection.id);
                      setSelectedCollections(next);
                      handleChange(
                        "applicableCollectionIds",
                        next.map((c) => c.id)
                      );
                    }}
                  >
                    {collection.name}
                  </Tag>
                ))}
                {selectedProducts.map((product) => (
                  <Tag
                    key={`product-${product.id}`}
                    closable
                    onClose={() => {
                      const next = selectedProducts.filter((p) => p.id !== product.id);
                      setSelectedProducts(next);
                      handleChange(
                        "applicableProductIds",
                        next.map((p) => p.id)
                      );
                    }}
                  >
                    {product.name}
                  </Tag>
                ))}
              </Space>
            </Space>

            <Space direction="vertical" size={6}>
              <Typography.Text strong>제외 대상</Typography.Text>
              <Space>
                <Button onClick={() => setExcludedBrandModalOpen(true)}>브랜드 선택</Button>
                <Button onClick={() => setExcludedCollectionModalOpen(true)}>컬렉션 선택</Button>
                <Button onClick={() => setExcludedProductModalOpen(true)}>상품 선택</Button>
              </Space>

              <Space wrap>
                {selectedExcludedBrands.map((brand) => (
                  <Tag
                    key={`excluded-brand-${brand.id}`}
                    color="red"
                    closable
                    onClose={() => {
                      const next = selectedExcludedBrands.filter((b) => b.id !== brand.id);
                      setSelectedExcludedBrands(next);
                      handleChange(
                        "excludedBrandIds",
                        next.map((b) => b.id)
                      );
                    }}
                  >
                    {brand.name}
                  </Tag>
                ))}
                {selectedExcludedCollections.map((collection) => (
                  <Tag
                    key={`excluded-collection-${collection.id}`}
                    color="red"
                    closable
                    onClose={() => {
                      const next = selectedExcludedCollections.filter(
                        (c) => c.id !== collection.id
                      );
                      setSelectedExcludedCollections(next);
                      handleChange(
                        "excludedCollectionIds",
                        next.map((c) => c.id)
                      );
                    }}
                  >
                    {collection.name}
                  </Tag>
                ))}
                {selectedExcludedProducts.map((product) => (
                  <Tag
                    key={`excluded-product-${product.id}`}
                    color="red"
                    closable
                    onClose={() => {
                      const next = selectedExcludedProducts.filter((p) => p.id !== product.id);
                      setSelectedExcludedProducts(next);
                      handleChange(
                        "excludedProductIds",
                        next.map((p) => p.id)
                      );
                    }}
                  >
                    {product.name}
                  </Tag>
                ))}
              </Space>
            </Space>

            <AppInputNumber
              value={formData.issueLimit}
              onChange={(value) =>
                handleChange(
                  "issueLimit",
                  value === null || value === undefined ? null : Number(value)
                )
              }
              style={{ width: "100%" }}
              placeholder="발행개수 (미입력시 무제한)"
              min={0}
              max={99999999}
            />

            <DatePicker
              showTime
              value={expiredAtDayjs}
              onChange={(value) => setExpiredAtDayjs(value)}
              style={{ width: "100%" }}
              placeholder="만기일"
            />

            <AppInputNumber
              value={formData.availableDay}
              onChange={(value) => handleChange("availableDay", Number(value ?? 0))}
              style={{ width: "100%" }}
              placeholder="사용기한(일)"
            />

            <Input.TextArea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="설명"
              autoSize={{ minRows: 3 }}
            />
          </Space>
        )}
      </Modal>

      <BrandSelectModal
        open={brandModalOpen}
        selectedIds={formData.applicableBrandIds ?? []}
        selectedItems={selectedBrands}
        onClose={() => setBrandModalOpen(false)}
        onConfirm={(selected) => {
          setSelectedBrands(selected);
          handleChange(
            "applicableBrandIds",
            selected.map((item) => item.id)
          );
        }}
      />

      <CollectionSelectModal
        open={collectionModalOpen}
        selectedIds={formData.applicableCollectionIds ?? []}
        selectedItems={selectedCollections}
        onClose={() => setCollectionModalOpen(false)}
        onConfirm={(selected) => {
          setSelectedCollections(selected);
          handleChange(
            "applicableCollectionIds",
            selected.map((item) => item.id)
          );
        }}
      />

      <ProductSelectModal
        open={productModalOpen}
        selectedIds={formData.applicableProductIds ?? []}
        selectedItems={selectedProducts}
        onClose={() => setProductModalOpen(false)}
        onConfirm={(selected) => {
          setSelectedProducts(selected);
          handleChange(
            "applicableProductIds",
            selected.map((item) => item.id)
          );
        }}
      />

      <BrandSelectModal
        open={excludedBrandModalOpen}
        selectedIds={formData.excludedBrandIds ?? []}
        selectedItems={selectedExcludedBrands}
        onClose={() => setExcludedBrandModalOpen(false)}
        onConfirm={(selected) => {
          setSelectedExcludedBrands(selected);
          handleChange(
            "excludedBrandIds",
            selected.map((item) => item.id)
          );
        }}
      />

      <CollectionSelectModal
        open={excludedCollectionModalOpen}
        selectedIds={formData.excludedCollectionIds ?? []}
        selectedItems={selectedExcludedCollections}
        onClose={() => setExcludedCollectionModalOpen(false)}
        onConfirm={(selected) => {
          setSelectedExcludedCollections(selected);
          handleChange(
            "excludedCollectionIds",
            selected.map((item) => item.id)
          );
        }}
      />

      <ProductSelectModal
        open={excludedProductModalOpen}
        selectedIds={formData.excludedProductIds ?? []}
        selectedItems={selectedExcludedProducts}
        onClose={() => setExcludedProductModalOpen(false)}
        onConfirm={(selected) => {
          setSelectedExcludedProducts(selected);
          handleChange(
            "excludedProductIds",
            selected.map((item) => item.id)
          );
        }}
      />
    </>
  );
}
